"use strict";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Box, Button, Grid, Group, Input } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconRefresh, IconSearch, IconTimeDuration15 } from "@tabler/icons-react";
import {
    ColDef,
    GridReadyEvent,
    IDatasource,
    IGetRowsParams,
    InfiniteRowModelModule,
    ModuleRegistry
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { paginationPageSizeSelector, repositoryDelivery } from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { MessageResponse } from "../../../model/MessageResponse";

// Đăng ký module Infinite Row Model
ModuleRegistry.registerModules([InfiniteRowModelModule]);

interface DeliveryNotSyncOrder {
    soCt: string;
    trangThai: string;
    ngayYeuCau: string;
    ghiChu: string | null;
    loai: number;
}
const DeliveryNotSync = () => {
    // Không cần state rowData hay loading thủ công nữa, AG Grid tự quản lý
    const gridRef = useRef<AgGridReact<any>>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(800);
    const [orderCode, setOrderCode] = useState("");
    const containerStyle = useMemo(() => ({ width: "100%", height: height }), [height]);
    const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
    const sizeGrid = { base: 12, sm: 6, md: 6, lg: 3 };
    const [visible, { toggle, close, open }] = useDisclosure(false);

    // Form Filter
    const form = useForm<{
        keySearch: string;
        NgayYeuCauTu: string | null;
        NgayYeuCauDen: string | null;
    }>({
        initialValues: {
            keySearch: "",
            NgayYeuCauTu: dayjs().subtract(10, 'day').format("YYYY-MM-DD"),
            NgayYeuCauDen: dayjs().format("YYYY-MM-DD"),
        },
    });

    // Auto resize height
    useEffect(() => {
        const updateHeight = () => {
            const headerHeight = headerRef.current?.offsetHeight || 0;
            const newHeight = window.innerHeight - (210 + headerHeight);
            setHeight(newHeight);
        };
        setTimeout(updateHeight, 0);
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    // --- LOGIC GỌI API (Được gọi bởi Datasource) ---
    const fetchData = async (startRow: number, endRow: number) => {
        try {
            // Tính toán Skip và Take dựa trên params của AG Grid
            const skip = startRow;
            const take = endRow - startRow; // Thường là 100

            let url = `/api/v1/VDeliveryNotExistReport/get-list?Skip=${skip}&Take=${take}`;
            const values = form.getValues();
            if (values.NgayYeuCauTu) url += `&NgayYeuCauTu=${values.NgayYeuCauTu}`;
            if (values.NgayYeuCauDen) url += `&NgayYeuCauDen=${values.NgayYeuCauDen}`;
            if (values.keySearch) url += `&KeySearch=${values.keySearch.trim()}`;
            const response = await repositoryDelivery.get<MessageResponse<any[]>>(url);
            return response;
        } catch (error) {
            console.error("API Error:", error);
            return null;
        }
    };

    // --- TẠO DATASOURCE CHO INFINITE ROW MODEL ---
    const getDatasource = useCallback((): IDatasource => {
        return {
            getRows: async (params: IGetRowsParams) => {
                const { startRow, endRow } = params;
                gridRef.current?.api?.showLoadingOverlay();
                try {
                    const response = await fetchData(startRow, endRow);

                    if (response && response.success) {
                        const data = response.data || [];
                        const totalCount = (response as any).totalCount ?? 0;
                        gridRef.current?.api?.hideOverlay();
                        params.successCallback(data, totalCount);
                    } else {
                        params.failCallback();
                        gridRef.current?.api?.hideOverlay();
                    }
                } catch (e) {
                    params.failCallback();
                    gridRef.current?.api?.hideOverlay();
                }
            }
        };
    }, [form.getValues()]); // Re-create datasource khi form values thay đổi (thực ra ko cần thiết nếu dùng setDatasource ở handleSearch)

    // --- SỰ KIỆN BẤM TÌM KIẾM ---
    const handleSearch = useCallback(() => {
        form.setErrors({});
        const values = form.getValues();
        if (values.NgayYeuCauTu && values.NgayYeuCauDen && dayjs(values.NgayYeuCauTu).isAfter(dayjs(values.NgayYeuCauDen))) {
            form.setFieldError("NgayYeuCauTu", "Ngày yêu cầu từ phải nhỏ hơn hoặc bằng ngày yêu cầu đến");
            return;
        }

        if (gridRef.current && gridRef.current.api) {
            // Set lại datasource mới sẽ kích hoạt Grid reset về trang 1 và gọi lại getRows với filter mới
            gridRef.current.api.setGridOption('datasource', getDatasource());
        }
    }, [form, getDatasource]);

    // --- ON GRID READY ---
    const onGridReady = useCallback((params: GridReadyEvent) => {
        // Gán datasource ban đầu
        params.api.setGridOption('datasource', getDatasource());
    }, [getDatasource]);


    // --- CÁC HÀM TIỆN ÍCH KHÁC GIỮ NGUYÊN ---
    const getRowId = useCallback((params: any) => {
        const d = params.data || {};
        return `${d.soCt}_${d.ngayYeuCau}_${d.trangThai}`.replace(/(^_+|_+$)/g, "") || Math.random().toString();
    }, []);

    const getRowStyle = useCallback((params: any) => {
        if (params.node.rowPinned) {
            return {
                backgroundColor: '#e7f5ff',
                fontWeight: 700,
                fontSize: '14px',
                borderTop: '3px solid #15aabf',
            };
        }
        return undefined;
    }, []);



    const handlePushOrder = useCallback(async (orderCode: string) => {
        const res = await repositoryDelivery.post<MessageResponse<any>>(
            "/api/v1/OrderHeader/push-order-header-to-delivery",
            {
                orderCode: orderCode,
            }
        );

        if (res && res.success) {
            setOrderCode("");
            NotificationExtension.Success("Đã cập nhật đơn giao hàng !");
        }
    }, []);

    // Ngắn gọn: double-click hàng -> copy soCt
    const handleRowDoubleClicked = useCallback((e: any) => {
        const code = e?.data?.soCt;
        if (!code) return;
        try {
            if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(code);
            else {
                const ta = document.createElement('textarea');
                ta.value = code;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            NotificationExtension.Success('Đã sao chép số chứng từ');
        } catch (err) {
            console.error('Copy failed', err);
        }
    }, []);

    const columnDefs = useMemo<ColDef[]>(() => [
        { field: "soCt", headerName: "Số chứng từ", width: 160, valueFormatter: (p: any) => p.value ?? "" },
        { field: "trangThai", headerName: "Trạng thái", width: 300, valueFormatter: (p: any) => (p.value != null ? String(p.value) : "") },
        { field: "ngayYeuCau", headerName: "Ngày yêu cầu", width: 200, valueFormatter: (p: any) => (p.value != null ? formatDateTransfer(p.value) : "") },
        { field: "ghiChu", headerName: "Ghi chú", minWidth: 350 },
        {
            field: "loai",
            headerName: "Loại",
            width: 120,
            valueFormatter: (p: any) => {
                if (p.value === 1) return "Đơn hàng";
                if (p.value === 2) return "Điều chuyển";
                return p.value ?? "";
            }
        },
        {
            field: "action",
            headerName: "Thao tác",
            width: 120,
            pinned: "right",
            cellRenderer: (params: any) => {
                console.log("Loggparams.data", params.data);
                if (!params.data) return null;
                return (
                    <Group gap="xs" justify="center" wrap="nowrap">
                        <Button
                            variant="subtle"
                            color="green"
                            size="compact-xs"
                            onClick={() => handlePushOrder(params.data.soCt)}
                            title="Đồng bộ đơn hàng"
                        >
                            <IconRefresh size={20} />
                        </Button>
                    </Group>
                );
            }
        }
    ], []);

    const defaultColDef = useMemo<ColDef>(() => {
        return {
            sortable: false, // Infinite model server-side sort hơi phức tạp, tạm tắt hoặc cần handle trong getRows
            resizable: true,
        };
    }, []);

    return (
        <Box pos={"relative"} style={containerStyle}>
            <LoadingOverlayCustom visible={visible} />

            <div style={{ border: "1px solid #dee2e6", padding: "10px", overflowX: "hidden" }}>
                <Group justify="space-between" gap={0}>
                    <BreadcrumbLayout1 />
                    {/* <Flex
                        gap="md"
                        justify={{ sm: "flex-end", lg: "flex-end" }}
                        align="center"
                        direction="row"
                        wrap="wrap"
                    >
                        <Menu shadow="md" width={200} closeOnItemClick={false}>
                            <Menu.Target>
                                <Button
                                    leftSection={<IconClipboardPlus size={14} />}
                                    color="green"
                                    variant="outline"
                                >
                                    Đồng bộ đơn hàng
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item closeMenuOnClick={false}>
                                    <TextInput
                                        placeholder="Nhập mã đơn hàng"
                                        value={orderCode}
                                        onChange={(e) => setOrderCode(e.currentTarget.value)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <Button
                                        variant="filled"
                                        color="green"
                                        mt={10}
                                        w={"100%"}
                                        onClick={() => handlePushOrder()}
                                    >
                                        Đồng bộ
                                    </Button>
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </Flex> */}
                </Group>
                <Grid mt={10}>
                    {/* GIỮ NGUYÊN UI FILTER NHƯ CŨ */}
                    <Grid.Col span={sizeGrid}>
                        <Input placeholder="Nhập tìm kiếm" leftSection={<IconSearch size={20} color="#15aabf" />} value={form.getValues().keySearch} onChange={e => form.setFieldValue("keySearch", e.target.value)} onKeyDown={async (e) => { if (e.key === "Enter") handleSearch(); }} />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <DatePickerInput size="sm" placeholder="Chọn ngày yêu cầu từ" locale="vi" valueFormat="DD/MM/YYYY" leftSection={<IconTimeDuration15 size={20} color="#15aabf" />} {...form.getInputProps("NgayYeuCauTu")} value={form.getValues().NgayYeuCauTu ? dayjs(form.getValues().NgayYeuCauTu, "YYYY-MM-DD").toDate() : null} onChange={(date) => form.setFieldValue("NgayYeuCauTu", date ? dayjs(date).format("YYYY-MM-DD") : null)} clearable />
                    </Grid.Col>
                    <Grid.Col span={sizeGrid}>
                        <DatePickerInput size="sm" placeholder="Chọn ngày yêu cầu đến" locale="vi" valueFormat="DD/MM/YYYY" leftSection={<IconTimeDuration15 size={20} color="#15aabf" />} {...form.getInputProps("NgayYeuCauDen")} value={form.getValues().NgayYeuCauDen ? dayjs(form.getValues().NgayYeuCauDen, "YYYY-MM-DD").toDate() : null} onChange={(date) => form.setFieldValue("NgayYeuCauDen", date ? dayjs(date).format("YYYY-MM-DD") : null)} clearable />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 0.9 }}>
                        <Button color="blue" variant="outline" onClick={handleSearch}>Tìm kiếm</Button>
                    </Grid.Col>
                </Grid>
            </div>

            <div style={gridStyle} className={"ag-theme-quartz"}>
                <AgGridReact<DeliveryNotSyncOrder>
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}

                    // --- CẤU HÌNH QUAN TRỌNG CHO INFINITE ROW MODEL ---
                    rowModelType={"infinite"} // Chế độ load theo trang từ server

                    // Kích thước mỗi lần gọi API (Take = 100)
                    cacheBlockSize={100}

                    // Số dòng tối đa giữ trong cache trình duyệt (tùy chỉnh để tối ưu RAM)
                    maxBlocksInCache={10}

                    // Hiển thị phân trang
                    pagination={true}
                    paginationPageSize={100} // Phải khớp hoặc là bội số của cacheBlockSize
                    paginationPageSizeSelector={paginationPageSizeSelector} // [100, 200, 500]

                    onGridReady={onGridReady}
                    onRowDoubleClicked={handleRowDoubleClicked}
                    getRowId={getRowId}
                    getRowStyle={getRowStyle}
                    localeText={AG_GRID_LOCALE_VN}
                />
            </div>
        </Box >
    );
};
export default DeliveryNotSync;